import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  CircularProgress,
  useTheme,
  Avatar,
  Chip
} from '@mui/material';
import { 
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { supabase } from '../../../lib/supabase';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title, 
  Tooltip, 
  Legend,
  Filler
} from 'chart.js';
import { Doughnut, Bar, Line } from 'react-chartjs-2';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';

// Register Chart.js components
ChartJS.register(
  CategoryScale, 
  LinearScale, 
  BarElement, 
  ArcElement,
  PointElement,
  LineElement,
  Title, 
  Tooltip, 
  Legend,
  Filler
);

const ReputationChartsPublic = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [stats, setStats] = useState({
    averageRating: 0,
    totalReviews: 0,
    positivePercentage: 0,
    excellentPercentage: 0
  });

  useEffect(() => {
    fetchPublicAnalytics();
  }, []);

  const fetchPublicAnalytics = async () => {
    try {
      setLoading(true);
      
      // Try RPC first (most efficient - single query with server-side aggregation)
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_public_feedback_analytics');
      
      if (!rpcError && rpcData) {
        console.log('[ReputationChartsPublic] Using RPC data:', rpcData);
        setAnalyticsData(rpcData);
        
        const { stats: rpcStats } = rpcData;
        const totalReviews = rpcStats.totalReviews || 0;
        setStats({
          averageRating: parseFloat(rpcStats.averageRating) || 0,
          totalReviews,
          positivePercentage: totalReviews > 0 ? ((rpcStats.positiveCount / totalReviews) * 100).toFixed(0) : 0,
          excellentPercentage: totalReviews > 0 ? ((rpcStats.excellentCount / totalReviews) * 100).toFixed(0) : 0
        });
        return;
      }
      
      // Fallback: fetch minimal data if RPC not available
      console.log('[ReputationChartsPublic] RPC not available, using fallback');
      const { data, error } = await supabase
        .from('feedback')
        .select('rating, created_at, platform', { count: 'exact' })
        .eq('is_public', true)
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;

      // Build analytics from limited data
      const fallbackAnalytics = buildFallbackAnalytics(data || []);
      setAnalyticsData(fallbackAnalytics);
      calculateStats(data || []);
    } catch (error) {
      console.error('Error fetching public feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildFallbackAnalytics = (data) => {
    const ratingDist = { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 };
    data.forEach(item => {
      if (item.rating >= 1 && item.rating <= 5) {
        ratingDist[item.rating.toString()]++;
      }
    });
    return { ratingDistribution: ratingDist, monthlyTrend: [] };
  };

  const calculateStats = (data) => {
    if (data.length === 0) {
      setStats({ averageRating: 0, totalReviews: 0, positivePercentage: 0, excellentPercentage: 0 });
      return;
    }
    const totalRating = data.reduce((sum, item) => sum + (item.rating || 0), 0);
    const averageRating = (totalRating / data.length).toFixed(1);
    const positiveCount = data.filter(item => item.rating >= 4).length;
    const excellentCount = data.filter(item => item.rating === 5).length;
    
    setStats({
      averageRating: parseFloat(averageRating),
      totalReviews: data.length,
      positivePercentage: ((positiveCount / data.length) * 100).toFixed(0),
      excellentPercentage: ((excellentCount / data.length) * 100).toFixed(0)
    });
  };

  // Rating Distribution Chart Data (uses pre-aggregated data from RPC)
  const getRatingDistributionData = () => {
    const dist = analyticsData?.ratingDistribution || { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 };
    const ratingCounts = [
      parseInt(dist['1']) || 0,
      parseInt(dist['2']) || 0,
      parseInt(dist['3']) || 0,
      parseInt(dist['4']) || 0,
      parseInt(dist['5']) || 0
    ];

    return {
      labels: ['1★', '2★', '3★', '4★', '5★'],
      datasets: [{
        label: 'Reviews',
        data: ratingCounts,
        backgroundColor: [
          '#F44336', // Red for 1 star
          '#FF9800', // Orange for 2 stars
          '#FFC107', // Yellow for 3 stars
          '#8BC34A', // Light green for 4 stars
          '#4CAF50', // Green for 5 stars
        ],
        borderWidth: 0
      }]
    };
  };

  // Platform Breakdown Chart Data (uses pre-aggregated data from RPC)
  const getPlatformBreakdownData = () => {
    const platformBreakdown = analyticsData?.platformBreakdown || [];
    
    if (platformBreakdown.length > 0) {
      return {
        labels: platformBreakdown.map(p => p.platform_name),
        datasets: [{
          label: 'Reviews by Platform',
          data: platformBreakdown.map(p => parseInt(p.review_count) || 0),
          backgroundColor: theme.palette.primary.main,
          borderColor: theme.palette.primary.dark,
          borderWidth: 1
        }]
      };
    }
    
    // Fallback: empty chart
    return {
      labels: ['Google', 'Yelp', 'Healthgrades'],
      datasets: [{
        label: 'Reviews by Platform',
        data: [0, 0, 0],
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.dark,
        borderWidth: 1
      }]
    };
  };

  // Rating Trend Chart Data (Last 6 months - uses pre-aggregated RPC data)
  const getRatingTrendData = () => {
    const monthlyTrend = analyticsData?.monthlyTrend || [];
    
    // Use RPC monthly data if available
    if (monthlyTrend.length > 0) {
      return {
        labels: monthlyTrend.map(m => m.month_name),
        datasets: [{
          label: 'Average Rating',
          data: monthlyTrend.map(m => parseFloat(m.avg_rating) || 0),
          borderColor: theme.palette.primary.main,
          backgroundColor: `${theme.palette.primary.main}20`,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6
        }]
      };
    }
    
    // Fallback: generate empty 6 month chart
    const monthsData = [];
    const today = new Date();
    for (let i = 5; i >= 0; i--) {
      const monthDate = subMonths(today, i);
      monthsData.push({
        month: format(monthDate, 'MMM'),
        rating: 0
      });
    }

    return {
      labels: monthsData.map(m => m.month),
      datasets: [{
        label: 'Average Rating',
        data: monthsData.map(m => m.rating),
        borderColor: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}20`,
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6
      }]
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Stats Cards Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Avatar sx={{ bgcolor: '#4CAF50', width: 40, height: 40, mx: 'auto', mb: 1 }}>
                <StarIcon fontSize="small" />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#4CAF50' }}>
                {stats.averageRating}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Avg Rating
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40, mx: 'auto', mb: 1 }}>
                <PublicIcon fontSize="small" />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                {stats.totalReviews}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Reviews
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Avatar sx={{ bgcolor: '#8BC34A', width: 40, height: 40, mx: 'auto', mb: 1 }}>
                <TrendingUpIcon fontSize="small" />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#8BC34A' }}>
                {stats.positivePercentage}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Positive (4-5★)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Avatar sx={{ bgcolor: '#FF9800', width: 40, height: 40, mx: 'auto', mb: 1 }}>
                <StarIcon fontSize="small" />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#FF9800' }}>
                {stats.excellentPercentage}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Excellent (5★)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3}>
        {/* Rating Distribution */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary }}>
                Rating Distribution
              </Typography>
              <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {stats.totalReviews > 0 ? (
                  <Doughnut 
                    data={getRatingDistributionData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: {
                            boxWidth: 12,
                            padding: 10,
                            font: { size: 10 }
                          }
                        }
                      }
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">No data available</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Platform Breakdown */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary }}>
                Reviews by Platform
              </Typography>
              <Box sx={{ height: 200 }}>
                {stats.totalReviews > 0 ? (
                  <Bar 
                    data={getPlatformBreakdownData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      indexAxis: 'y',
                      plugins: {
                        legend: { display: false }
                      },
                      scales: {
                        x: {
                          beginAtZero: true,
                          ticks: { precision: 0 }
                        }
                      }
                    }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="body2" color="text.secondary">No data available</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Rating Trend */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary }}>
                Rating Trend (6 Months)
              </Typography>
              <Box sx={{ height: 200 }}>
                {stats.totalReviews > 0 ? (
                  <Line 
                    data={getRatingTrendData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          max: 5,
                          ticks: { stepSize: 1 }
                        }
                      }
                    }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="body2" color="text.secondary">No data available</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReputationChartsPublic;
